/**
 * API Module Index
 * Re-exports all API functions for convenient importing
 *
 * Usage:
 *   import { getOrder, updateTask, getDrive } from '@/api'
 */

// Client
import type {ListTask} from "@/types/Order";

import {supabase} from './client'
import {AssetFetchParams, AssetFetchResult} from "@/types/Asset";

export {supabase, backendClient, SUPABASE_URL, BACKEND_URL} from './client'

export interface ListParams {
    page?: number
    itemsPerPage?: number
    sortBy?: string
    sortOrder?: string
    filters?: Record<string, any>
}

export interface ListResponse<T> {
    items: T[]
    total: number
}


export async function getRowsFiltered<T>(params: ListParams, table: string, rows?: string): Promise<ListResponse<T>> {
    const {page = 1, itemsPerPage = 10, sortBy = 'id', sortOrder = 'asc', filters = {}} = params

    // Leere Filter entfernen
    const cleanFilters: Record<string, string> = {}
    for (const [key, value] of Object.entries(filters)) {
        if (value !== undefined && value !== null && value !== '') {
            cleanFilters[key] = String(value)
        }
    }

    const {data, error} = await supabase.rpc('filter_rows', {
        p_table: table,
        p_filters: cleanFilters,
        p_sort_column: sortBy,
        p_sort_order: sortOrder.toLowerCase(),
        p_limit: itemsPerPage,
        p_offset: (page - 1) * itemsPerPage,
        p_select: rows
    })

    if (error) throw error

    return {
        items: data?.items || [],
        total: data?.total || 0
    }
}

/**
 * Spalten-Konfiguration für Filter
 * - enum: Exakter Match, Wildcards werden entfernt, Wert wird uppercase
 * - numeric: Exakter Match oder Range (kein ILIKE möglich)
 * - text: ILIKE-Suche mit Wildcards
 */
const ENUM_COLUMNS = new Set(['state', 'work_group', 'priority', 'role'])
const NUMERIC_COLUMNS = new Set(['id', 'order_id', 'task_id', 'sequence', 'asset_id', 'duration', 'scheduled_duration'])

/**
 * Universelle Filter-Funktion für Supabase Queries
 *
 * Syntax:
 *   *text*     - Wildcard (ILIKE %text%)
 *   >10, <10   - Vergleiche
 *   10..50     - Range
 *   a,b,c      - OR-Liste (IN)
 *   a&b        - AND-Liste
 *   !value     - Negation (NOT)
 *   !=value    - Negation (alternativ)
 */
export function applyFilter(query: any, column: string, value: string) {
    if (!value || value === '') return query

    const strValue = String(value).trim()
    const isEnum = ENUM_COLUMNS.has(column)

    // === NEGATION ===
    if (strValue.startsWith('!') || strValue.startsWith('!=')) {
        const negated = strValue.replace(/^!=?/, '').trim()

        // Mehrere negierte Werte: !BEEND,GESCHL
        if (negated.includes(',')) {
            let values = negated.split(',').map(v => v.trim().replace(/^!=?/, ''))
            if (isEnum) values = values.map(v => v.replace(/\*/g, '').toUpperCase())
            return query.not(column, 'in', `(${values.join(',')})`)
        }

        // Wildcard-Negation (nur für nicht-ENUMs)
        if (negated.includes('*') && !isEnum) {
            return query.not(column, 'ilike', negated.replace(/\*/g, '%'))
        }

        // Einfache Negation
        const cleanValue = isEnum ? negated.replace(/\*/g, '').toUpperCase() : negated
        return query.neq(column, cleanValue)
    }

    // === RANGE: 10..50 oder 2025-01-01..2025-12-31 ===
    if (strValue.includes('..')) {
        const [min, max] = strValue.split('..')
        if (min) query = query.gte(column, min.trim())
        if (max) query = query.lte(column, max.trim())
        return query
    }

    // === VERGLEICHE: >=, <=, >, < ===
    if (strValue.startsWith('>=')) return query.gte(column, strValue.slice(2).trim())
    if (strValue.startsWith('<=')) return query.lte(column, strValue.slice(2).trim())
    if (strValue.startsWith('>')) return query.gt(column, strValue.slice(1).trim())
    if (strValue.startsWith('<')) return query.lt(column, strValue.slice(1).trim())

    // === OR-LISTE: value1,value2,value3 ===
    if (strValue.includes(',') && !strValue.includes('&')) {
        let values = strValue.split(',').map(v => v.trim())
        if (isEnum) values = values.map(v => v.replace(/\*/g, '').toUpperCase())
        return query.in(column, values)
    }

    // === AND-LISTE: value1&value2 ===
    if (strValue.includes('&')) {
        const values = strValue.split('&').map(v => v.trim())
        for (const v of values) {
            query = applyFilter(query, column, v)
        }
        return query
    }

    // === ENUM: Wildcards entfernen, uppercase, exakter Match ===
    if (isEnum) {
        const cleanValue = strValue.replace(/\*/g, '').toUpperCase()
        return query.eq(column, cleanValue)
    }

    // === NUMERISCHE SPALTEN: Exakter Match oder Prefix-Range ===
    if (NUMERIC_COLUMNS.has(column)) {
        // Wildcards entfernen - bei Zahlen nicht sinnvoll
        const cleanValue = strValue.replace(/\*/g, '')

        // Wenn reine Zahl: Prefix-Suche via Range
        // "1" findet 1, 10, 11, 100, etc.
        if (/^\d+$/.test(cleanValue)) {
            const num = parseInt(cleanValue, 10)
            const magnitude = Math.pow(10, cleanValue.length)
            // Suche alle die mit dieser Zahl beginnen
            return query.gte(column, num).lt(column, num + magnitude)
        }

        // Fallback: exakter Match
        return query.eq(column, cleanValue)
    }

    // === WILDCARD: *text* (nur für Text-Spalten) ===
    if (strValue.includes('*')) {
        return query.ilike(column, strValue.replace(/\*/g, '%'))
    }

    // === STANDARD: ILIKE Teilsuche für Text-Spalten ===
    return query.ilike(column, `%${strValue}%`)
}


// Orders
export {
    getOrder,
    getOrdersWithProgress,
    getOrdersFiltered,
    //getOrderTitles,
    createOrder,
    updateOrder,
    deleteOrder,
} from './orders'
export type {OrderWithProgress, OrderFilters} from './orders'

// Tasks
export {
    getTask,
    getTasksForOrder,
    getTasksWithDetails,
    getTasksFiltered,
    getTaskTitles,
    createTask,
    updateTask,
    deleteTask,
    isTaskStartable,
    addFeedback,
    updateFeedback,
    deleteFeedback,
} from './tasks'

// Drives
export {
    getDrive,
    getDrives,
    getDrivesFiltered,
    getDrivePosition,
    normalizeAssetId,
    getDrivesList,
    createDrive,
    updateDrive,
    deleteDrive,
} from './drives'

// Dashboard
export {
    getDashboardData,
    getDashboardStats,
    getUserDashboardStats,
    getReportingOverview,
    getReportData,
    getOutages,
} from './dashboard'
export type {DashboardStats, ReportingOverview} from './dashboard'

// Outages
export {
    getOutagesFiltered,
    getOutageByCode,
    getOutageOrders,
    getOutageTasks,
    getOutageChecklists,
    getCompressedOutages,
} from './outages'

// LOTO (Freischaltpläne, Bereiche, Schnell-Rückmeldung)
export {
    getLotoPlans,
    createLotoPlan,
    updateLotoPlan,
    deleteLotoPlan,
    uploadPlanImage,
    getPlanImageUrl,
    getLotoAreas,
    getLotoAreasWithStats,
    createLotoArea,
    updateLotoArea,
    deleteLotoArea,
    quickCompleteTask,
    useLotoAreaStore,
} from './loto'

// Shifts
export {
    getShiftsForRange,
    upsertShift,
    upsertShiftsBatch,
    deleteShift,
    getShiftsFiltered,
} from './shifts'
export type { UserShift } from './shifts'
